import React, { useState } from 'react';
import { 
  BookOpen, 
  CheckCircle, 
  XCircle, 
  Calendar,
  Plus,
  Edit,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Eye
} from 'lucide-react';
import { Button, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import { useAdminFAQ } from '../../hooks/useAdminFAQ';
import { formatDate } from '../../utils/dateUtils';

const AdminFAQ = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingFAQ, setEditingFAQ] = useState(null);
  const {
    faqs,
    isLoading,
    error,
    filters,
    activeStatusOptions,
    handleFilterChange,
    handleSearch,
    statistics: stats,
    toggleStatus,
    isToggling,
    deleteFAQ,
    isDeleting
  } = useAdminFAQ();

  // Define table columns - truncated to prevent layout overflow
  const columns = [
    {
      key: 'question',
      label: 'Question',
      sortable: true,
      width: 'w-48',
      render: (faq) => (
        <div className="max-w-full overflow-hidden">
          <p className="text-sm font-medium text-gray-900 line-clamp-2 break-words" title={faq.question}>
            {faq.question || 'N/A'}
          </p>
        </div>
      )
    },
    {
      key: 'answer',
      label: 'Answer',
      sortable: false,
      width: 'w-48',
      render: (faq) => (
        <div className="max-w-full overflow-hidden">
          <p className="text-sm text-gray-900 line-clamp-2 break-words" title={faq.answer}>
            {faq.answer || 'N/A'}
          </p>
        </div>
      )
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      width: 'w-32',
      render: (faq) => (
        <span className="text-sm text-gray-900 capitalize">
          {faq.category || 'General'}
        </span>
      )
    },
    {
      key: 'order',
      label: 'Order',
      sortable: true,
      width: 'w-24',
      render: (faq) => (
        <span className="text-sm font-medium text-gray-900">
          {faq.order || 0}
        </span>
      )
    },
    {
      key: 'stats',
      label: 'Stats',
      sortable: false,
      width: 'w-32',
      render: (faq) => (
        <div className="text-xs text-gray-600">
          <div>Views: {faq.views_count || 0}</div>
          <div>Helpful: {faq.helpful_count || 0}</div>
        </div>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      width: 'w-32',
      render: (faq) => (
        <div className="flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">
              {formatDate(faq.created_at, 'medium')}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: true,
      width: 'w-24',
      render: (faq) => (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          faq.is_active 
            ? 'bg-green-100 text-green-800' 
            : 'bg-gray-100 text-gray-800'
        }`}>
          {faq.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    }
  ];

  // Define table actions
  const actions = [
    {
      label: 'Edit',
      icon: Edit,
      variant: 'default',
      onClick: (faq) => {
        setEditingFAQ(faq);
        setShowCreateModal(true);
      }
    },
    {
      label: faq => faq.is_active ? 'Deactivate' : 'Activate',
      icon: faq => faq.is_active ? ToggleLeft : ToggleRight,
      variant: 'default',
      onClick: (faq) => {
        toggleStatus(faq.id);
      }
    },
    {
      label: 'Delete',
      icon: Trash2,
      variant: 'danger',
      onClick: (faq) => {
        if (window.confirm('Are you sure you want to delete this FAQ?')) {
          deleteFAQ(faq.id);
        }
      }
    }
  ];

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
        <div className="bg-white border rounded-lg p-6">
          <div className="text-center">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading FAQs</h3>
            <p className="text-gray-600">
              {error?.response?.data?.message || 'Failed to load FAQs. Please try again later.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-6">
      <div className="space-y-3 sm:space-y-4">
        {/* Header */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
                <BookOpen className="w-6 h-6" />
                FAQ Management
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Manage frequently asked questions and help content
              </p>
            </div>
            <Button
              onClick={() => {
                setEditingFAQ(null);
                setShowCreateModal(true);
              }}
              className="flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create FAQ
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total FAQs</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total_faqs || 0}</p>
              </div>
              <BookOpen className="w-8 h-8 text-gray-400" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats.active_faqs || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Views</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{stats.total_views || 0}</p>
              </div>
              <Eye className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white border rounded-lg p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Helpful Votes</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{stats.total_helpful || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border rounded-lg p-3 sm:p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <SearchSelect
              label="Status"
              value={filters.is_active}
              onChange={(value) => handleFilterChange('is_active', value)}
              options={activeStatusOptions}
            />
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border rounded-lg p-3 sm:p-4 overflow-hidden">
          <DataTable
            data={faqs}
            columns={columns}
            actions={actions}
            isLoading={isLoading}
            searchPlaceholder="Search by question, answer, or category..."
            onSearch={handleSearch}
            emptyMessage="No FAQs found"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminFAQ;

